import { useState, useCallback } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import {
  Shield,
  Loader2,
  AlertCircle,
  Check,
  FileSignature,
  ArrowRight,
} from "lucide-react";
import { useVerifyToken, useSignNda, useSkipNda } from "@/api/client";
import { setSession } from "@/lib/session";

type Step = "loading" | "error" | "welcome" | "nda" | "signing";

export default function VerifyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") ?? "";

  const { data, isLoading, error } = useVerifyToken(token);
  const signNdaMutation = useSignNda();
  const skipNdaMutation = useSkipNda();

  const [signerName, setSignerName] = useState("");
  const [agreed, setAgreed] = useState(false);

  const resolveStep = useCallback((): Step => {
    if (!token) return "error";
    if (isLoading) return "loading";
    if (error || !data?.ok) return "error";
    if (signNdaMutation.isPending || skipNdaMutation.isPending) return "signing";
    if (data.tenant.nda_required && data.tenant.nda_template_text) return "nda";
    return "welcome";
  }, [
    token,
    isLoading,
    error,
    data,
    signNdaMutation.isPending,
    skipNdaMutation.isPending,
  ]);

  const step = resolveStep();

  function handleSkipNda() {
    skipNdaMutation.mutate(
      { verificationToken: token },
      {
        onSuccess: (response) => {
          setSession(response.accessToken, data?.requesterName ?? "");
          navigate("/documents", { replace: true });
        },
      }
    );
  }

  function handleSignNda() {
    signNdaMutation.mutate(
      { verificationToken: token, signerName: signerName.trim() },
      {
        onSuccess: (response) => {
          setSession(response.accessToken, signerName.trim());
          navigate("/documents", { replace: true });
        },
      }
    );
  }

  // Loading state
  if (step === "loading") {
    return (
      <div className="flex flex-col items-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
        <p className="mt-4 text-sm text-gray-500">
          Verifying your access link...
        </p>
      </div>
    );
  }

  // Error / invalid token
  if (step === "error") {
    const errorMessage = !token
      ? "No verification token found in the URL."
      : "This verification link is invalid or has expired.";

    return (
      <div className="mx-auto max-w-md">
        <div className="flex flex-col items-center rounded-lg border border-red-200 bg-white px-6 py-12 text-center shadow-sm">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
            <AlertCircle className="h-7 w-7 text-red-500" />
          </div>
          <h2 className="mt-4 text-lg font-semibold text-gray-900">
            Verification Failed
          </h2>
          <p className="mt-2 text-sm text-gray-500">{errorMessage}</p>
          <Link
            to="/request"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-brand-primary px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-opacity hover:opacity-90"
          >
            Request New Access
          </Link>
        </div>
      </div>
    );
  }

  const companyName = data?.tenant.company_name ?? "Trust Center";
  const ndaRequired = data?.tenant.nda_required && !!data?.tenant.nda_template_text;
  const ndaText = data?.tenant.nda_template_text ?? "";
  const ndaCompanyName = data?.tenant.nda_company_name ?? companyName;
  const mutationError = signNdaMutation.error || skipNdaMutation.error;

  // Welcome page (no NDA required) - show let's go button
  if (step === "welcome") {
    return (
      <div className="mx-auto max-w-md">
        <div className="flex flex-col items-center rounded-lg border border-gray-200 bg-white px-6 py-12 text-center shadow-sm">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-primary/10">
            <Shield className="h-7 w-7 text-brand-primary" />
          </div>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">
            Welcome to {companyName} Trust Center
          </h2>
          <p className="mt-2 text-sm text-gray-500">
            Your email has been verified. You now have access to the security
            documentation.
          </p>

          {mutationError && (
            <div className="mt-4 w-full rounded-md border border-red-200 bg-red-50 px-3 py-2.5">
              <p className="text-sm text-red-700">
                {mutationError.message || "Something went wrong. Please try again."}
              </p>
            </div>
          )}

          <button
            onClick={handleSkipNda}
            disabled={skipNdaMutation.isPending}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-brand-primary px-6 py-2.5 text-sm font-medium text-white shadow-sm transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {skipNdaMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Granting access...
              </>
            ) : (
              <>
                Let's Go
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </button>

          <p className="mt-4 text-xs text-gray-400">
            By proceeding, you agree to the platform's Terms of Use and{" "}
            {companyName}'s Privacy Policy.
          </p>
        </div>
      </div>
    );
  }

  // NDA signing step
  const canSign = signerName.trim().length >= 2 && agreed;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      {/* Welcome header */}
      <div className="flex flex-col items-center text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-primary/10">
          <Shield className="h-7 w-7 text-brand-primary" />
        </div>
        <h1 className="mt-4 text-xl font-semibold text-gray-900">
          Welcome to {companyName} Trust Center
        </h1>
        <p className="mt-2 text-sm text-gray-500">
          Before accessing protected documents, please review and sign the
          Non-Disclosure Agreement below.
        </p>
      </div>

      {/* NDA document */}
      <div className="card overflow-hidden">
        <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-5 py-3">
          <FileSignature className="h-4 w-4 text-gray-500" />
          <h2 className="text-sm font-semibold text-gray-700">
            Non-Disclosure Agreement
          </h2>
          <span className="ml-auto rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
            Signature Required
          </span>
        </div>

        <div className="max-h-80 overflow-y-auto px-6 py-5">
          <div className="prose prose-sm max-w-none text-gray-700">
            <pre className="whitespace-pre-wrap font-serif text-sm leading-relaxed">
              {ndaText}
            </pre>
          </div>
        </div>

        <div className="border-t border-gray-200 bg-gray-50/50 px-6 py-5">
          <p className="mb-4 text-xs font-medium uppercase tracking-wide text-gray-400">
            Electronic Signature
          </p>

          {/* Signer name */}
          <div className="mb-4">
            <label
              htmlFor="signerName"
              className="block text-sm font-medium text-gray-700"
            >
              Type your full legal name
            </label>
            <input
              id="signerName"
              type="text"
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              placeholder="Jane Smith"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-gray-400 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
            />
          </div>

          {/* Agreement checkbox */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary/30"
            />
            <span className="text-sm text-gray-600">
              I have read and agree to the above Non-Disclosure Agreement on
              behalf of myself and my organization. I understand this constitutes
              a legally binding electronic signature with{" "}
              <strong>{ndaCompanyName}</strong>.
            </span>
          </label>

          {/* Error */}
          {mutationError && (
            <div className="mt-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" />
              <p className="text-sm text-red-700">
                {mutationError.message ||
                  "Something went wrong. Please try again."}
              </p>
            </div>
          )}

          {/* Sign button */}
          <button
            onClick={handleSignNda}
            disabled={!canSign || signNdaMutation.isPending}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-md bg-brand-primary px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {signNdaMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Signing...
              </>
            ) : (
              <>
                <Check className="h-4 w-4" />
                Sign & Continue
              </>
            )}
          </button>
        </div>
      </div>

      <p className="text-center text-xs text-gray-400">
        By proceeding, you agree to the platform's Terms of Use and{" "}
        {companyName}'s Privacy Policy.
      </p>
    </div>
  );
}
